import PostActionDropdown from "@components/common/content-actions/post-action-dropdown";
import { Icon, Spinner } from "@components/common/icon";
import { BanUpdateForm } from "@components/common/modal/mod-action-form-modal";
import { VoteButtonsCompact } from "@components/common/vote-buttons";
import { I18NextService } from "@services/index";
import {
  postIsInteractable,
  setIsoData,
  userNotLoggedInOrBanned,
} from "@utils/app";
import { share } from "@utils/browser";
import { futureDaysToUnixTime } from "@utils/date";
import { httpFrontendUrl } from "@utils/env";
import { unreadCommentsCount } from "@utils/helpers";
import {
  CrossPostParams,
  IsoDataOptionalSite,
  ShowBodyType,
  ShowMarkReadType,
} from "@utils/types";
import classNames from "classnames";
import { Link } from "inferno-router";
import {
  PostView,
  PersonView,
  MyUserInfo,
  LocalSite,
  CreatePostLike,
  MarkPostAsRead,
  Post,
  AddAdmin,
  AddModToCommunity,
  BanFromCommunity,
  BanPerson,
  BlockCommunity,
  BlockPerson,
  CreatePostReport,
  DeletePost,
  FeaturePost,
  HidePost,
  LockPost,
  NotePerson,
  PurgePerson,
  PurgePost,
  RemovePost,
  SavePost,
  TransferCommunity,
  CreatePostWarning,
} from "lemmy-js-client";

type PostActionBarProps = {
  postView: PostView;
  admins: PersonView[];
  showBody: ShowBodyType;
  showMarkRead: ShowMarkReadType;
  viewOnly: boolean;
  viewSource: boolean;
  myUserInfo: MyUserInfo | undefined;
  localSite: LocalSite;
  notificationRead?: boolean;
  markReadLoading: boolean;
  voteLoading: boolean;
  onPostVote: (form: CreatePostLike) => void;
  onScrollIntoCommentsClick: (e: MouseEvent) => void;
  onViewSource: () => void;
  onMarkPostAsRead: (form: MarkPostAsRead) => void;
  onEditClick: () => void;
  onPostReport: (form: CreatePostReport) => void;
  onBlockPerson: (form: BlockPerson) => void;
  onBlockCommunity: (form: BlockCommunity) => void;
  onLockPost: (form: LockPost) => void;
  onWarnPost: (form: CreatePostWarning) => void;
  onDeletePost: (form: DeletePost) => void;
  onRemovePost: (form: RemovePost) => void;
  onSavePost: (form: SavePost) => void;
  onFeaturePost: (form: FeaturePost) => void;
  onPurgePerson: (form: PurgePerson) => void;
  onPurgePost: (form: PurgePost) => void;
  onBanPersonFromCommunity: (form: BanFromCommunity) => void;
  onBanPerson: (form: BanPerson) => void;
  onAddModToCommunity: (form: AddModToCommunity) => void;
  onAddAdmin: (form: AddAdmin) => void;
  onTransferCommunity: (form: TransferCommunity) => void;
  onHidePost: (form: HidePost) => void;
  onPersonNote: (form: NotePerson) => void;
};

export function PostActionBar(props: PostActionBarProps, context: object) {
  const {
    postView,
    admins,
    showBody,
    onPostVote,
    onScrollIntoCommentsClick,
    viewOnly,
    viewSource,
    myUserInfo,
    localSite,
    showMarkRead,
    notificationRead,
    onMarkPostAsRead,
    markReadLoading,
    voteLoading,
  } = props;
  const { id } = postView.post;

  return (
    <div className="row row-cols-auto align-items-center g-3 justify-content-end justify-content-md-start">
      {showMarkRead === "main_bar" && (
        <div className="col">
          <PostMarkReadButton
            post={postView.post}
            read={notificationRead ?? false}
            loading={markReadLoading}
            onMarkRead={form => onMarkPostAsRead(form)}
          />
        </div>
      )}
      <div className="col text-muted">
        <CommentsButton
          postView={postView}
          type_="icon"
          onScrollIntoCommentsClick={onScrollIntoCommentsClick}
        />
      </div>
      {postIsInteractable(postView, viewOnly) && (
        <div className="col">
          <VoteButtonsCompact
            voteContentType={"post"}
            id={id}
            onVote={onPostVote}
            subject={postView.post}
            myVoteIsUpvote={postView.post_actions?.vote_is_upvote}
            myUserInfo={myUserInfo}
            localSite={localSite}
            disabled={userNotLoggedInOrBanned(myUserInfo)}
            loading={voteLoading}
          />
        </div>
      )}
      <div className="col">
        <PostActionDropdown
          postView={postView}
          community={postView.community}
          admins={admins}
          crossPostParams={crossPostParams(postView.post, myUserInfo)}
          myUserInfo={myUserInfo}
          viewSource={viewSource}
          showBody={showBody}
          viewOnly={viewOnly}
          showMarkRead={showMarkRead}
          onSave={() => handleSavePost(props)}
          onReport={reason => handleReport(props, reason)}
          onBlockPerson={() => handleBlockPerson(props)}
          onBlockCommunity={() => handleBlockCommunity(props)}
          onEdit={props.onEditClick}
          onDelete={() => handleDeletePost(props)}
          onLock={reason => handleModLock(props, reason)}
          onWarn={reason => handleWarnPost(props, reason)}
          onFeatureCommunity={() => handleModFeaturePostCommunity(props)}
          onFeatureLocal={() => handleModFeaturePostLocal(props)}
          onRemove={reason => handleRemove(props, reason)}
          onBanFromCommunity={form => handleModBanFromCommunity(props, form)}
          onAppointCommunityMod={() => handleAppointCommunityMod(props)}
          onTransferCommunity={() => handleTransferCommunity(props)}
          onBanFromSite={reason => handleModBanFromSite(props, reason)}
          onPurgeUser={reason => handlePurgePerson(props, reason)}
          onPurgeContent={reason => handlePurgePost(props, reason)}
          onAppointAdmin={() => handleAppointAdmin(props)}
          onHidePost={() => handleHidePost(props)}
          onPersonNote={props.onPersonNote}
          onViewSource={props.onViewSource}
          onSharePost={() =>
            handleShare(props.postView.post, setIsoData(context))
          }
          onMarkPostAsRead={() => handleMarkPostAsRead(props)}
        />
      </div>
    </div>
  );
}

type CommentsButtonTextOrIcon = "text" | "icon";
type CommentsButtonProps = {
  postView: PostView;
  type_: CommentsButtonTextOrIcon;
  onScrollIntoCommentsClick: (e: MouseEvent) => void;
};
export function CommentsButton({
  postView,
  type_,
  onScrollIntoCommentsClick,
}: CommentsButtonProps) {
  const title = I18NextService.i18n.t("number_of_comments", {
    count: Number(postView.post.comments),
    formattedCount: Number(postView.post.comments),
  });

  const count = postView.post.comments;
  const unreadCount = unreadCommentsCount(postView);

  return (
    <Link
      className={classNames("btn btn-sm text-muted", {
        "border-light-subtle": type_ === "icon",
        "btn-link ps-0 py-0": type_ === "text",
      })}
      title={title}
      to={`/post/${postView.post.id}?scrollToComments=true`}
      data-tippy-content={title}
      onClick={onScrollIntoCommentsClick}
    >
      {type_ === "icon" && <Icon icon="message-square" classes="me-1" inline />}
      {count}
      {type_ === "text" && <span> {I18NextService.i18n.t("comments")}</span>}
      {unreadCount && type_ === "text" && (
        <span className="ms-2 fst-italic">
          ({unreadCount} {I18NextService.i18n.t("new")})
        </span>
      )}
      {unreadCount && type_ === "icon" && (
        <span className="ms-2 badge text-bg-light">+{unreadCount}</span>
      )}
    </Link>
  );
}

type PostMarkReadButtonProps = {
  post: Post;
  read: boolean;
  loading: boolean;
  onMarkRead: (form: MarkPostAsRead) => void;
};
function PostMarkReadButton({
  post,
  read,
  loading,
  onMarkRead,
}: PostMarkReadButtonProps) {
  return (
    <button
      className="btn btn-sm border-light-subtle btn-animate text-muted"
      onClick={() => onMarkRead({ post_id: post.id, read: !read })}
      data-tippy-content={
        read
          ? I18NextService.i18n.t("mark_as_unread")
          : I18NextService.i18n.t("mark_as_read")
      }
      aria-label={
        read
          ? I18NextService.i18n.t("mark_as_unread")
          : I18NextService.i18n.t("mark_as_read")
      }
    >
      {loading ? (
        <Spinner />
      ) : (
        <Icon icon="check" classes={`icon-inline ${read && "text-success"}`} />
      )}
    </button>
  );
}

function crossPostParams(post: Post, myUserInfo?: MyUserInfo): CrossPostParams {
  const { name, url, alt_text, nsfw, language_id, thumbnail_url } = post;
  const crossPostParams: CrossPostParams = { name };

  if (url) {
    crossPostParams.url = url;
  }

  const crossPostBody_ = crossPostBody(post, myUserInfo);
  if (crossPostBody_) {
    crossPostParams.body = crossPostBody_;
  }

  if (alt_text) {
    crossPostParams.altText = alt_text;
  }

  if (nsfw) {
    crossPostParams.nsfw = nsfw ? "true" : "false";
  }

  if (language_id !== undefined) {
    crossPostParams.languageId = language_id;
  }

  if (thumbnail_url) {
    crossPostParams.customThumbnailUrl = thumbnail_url;
  }

  return crossPostParams;
}

function crossPostBody(
  post: Post,
  myUserInfo?: MyUserInfo,
): string | undefined {
  const body = post.body;

  let bodyOut: string | undefined;
  if (body) {
    // If its you're own post, don't include the x-posted-from line, or the quotes
    if (post.creator_id === myUserInfo?.local_user_view?.person?.id) {
      bodyOut = body;
    } else {
      bodyOut = `${I18NextService.i18n.t("cross_posted_from_url", { ap_id: post.ap_id })}
      \n\n${body.replace(/^/gm, "> ")}`;
    }
  } else {
    bodyOut = undefined;
  }

  return bodyOut;
}

async function handleShare(post: Post, isoData: IsoDataOptionalSite) {
  const { name, body, id } = post;
  await share({
    title: name,
    text: body?.slice(0, 50),
    url: httpFrontendUrl(`/post/${id}`, isoData),
  });
}

// TODO All these handlers should not have to exist. The PostActionsDropdown should push up the forms directly
function handleMarkPostAsRead(props: PostActionBarProps) {
  // Toggle the read, based on the existence of read_at
  const read = !props.postView.post_actions?.read_at;

  props.onMarkPostAsRead?.({
    post_id: props.postView.post.id,
    read,
  });
}

function handleReport(i: PostActionBarProps, reason: string) {
  return i.onPostReport({
    post_id: i.postView.post.id,
    reason,
  });
}

function handleBlockPerson(i: PostActionBarProps) {
  return i.onBlockPerson({
    person_id: i.postView.creator.id,
    block: true,
  });
}

function handleBlockCommunity(i: PostActionBarProps) {
  return i.onBlockCommunity({
    community_id: i.postView.community.id,
    block: true,
  });
}

function handleDeletePost(i: PostActionBarProps) {
  return i.onDeletePost({
    post_id: i.postView.post.id,
    deleted: !i.postView.post.deleted,
  });
}

function handleSavePost(i: PostActionBarProps) {
  return i.onSavePost({
    post_id: i.postView.post.id,
    save: !i.postView.post_actions?.saved_at,
  });
}

function handleRemove(i: PostActionBarProps, reason: string) {
  return i.onRemovePost({
    post_id: i.postView.post.id,
    removed: !i.postView.post.removed,
    reason,
  });
}

function handleModLock(i: PostActionBarProps, reason: string) {
  return i.onLockPost({
    post_id: i.postView.post.id,
    locked: !i.postView.post.locked,
    reason,
  });
}

function handleWarnPost(i: PostActionBarProps, reason: string) {
  return i.onWarnPost({
    post_id: i.postView.post.id,
    reason,
  });
}

function handleModFeaturePostLocal(i: PostActionBarProps) {
  return i.onFeaturePost({
    post_id: i.postView.post.id,
    featured: !i.postView.post.featured_local,
    feature_type: "local",
  });
}

function handleModFeaturePostCommunity(i: PostActionBarProps) {
  return i.onFeaturePost({
    post_id: i.postView.post.id,
    featured: !i.postView.post.featured_community,
    feature_type: "community",
  });
}

function handlePurgePost(i: PostActionBarProps, reason: string) {
  return i.onPurgePost({
    post_id: i.postView.post.id,
    reason,
  });
}

function handlePurgePerson(i: PostActionBarProps, reason: string) {
  return i.onPurgePerson({
    person_id: i.postView.creator.id,
    reason,
  });
}

function handleHidePost(i: PostActionBarProps) {
  return i.onHidePost({
    hide: !i.postView.post_actions?.hidden_at,
    post_id: i.postView.post.id,
  });
}

function handleModBanFromCommunity(i: PostActionBarProps, form: BanUpdateForm) {
  const {
    creator: { id: person_id },
    creator_banned_from_community,
    community: { id: community_id },
  } = i.postView;
  const ban = !creator_banned_from_community;
  const expires_at = futureDaysToUnixTime(form.daysUntilExpires);

  // If its an unban, restore all their data
  const shouldRemoveOrRestoreData = ban ? form.shouldRemoveOrRestoreData : true;

  return i.onBanPersonFromCommunity({
    community_id,
    person_id,
    ban,
    remove_or_restore_data: shouldRemoveOrRestoreData,
    reason: form.reason,
    expires_at,
  });
}

function handleModBanFromSite(i: PostActionBarProps, form: BanUpdateForm) {
  const {
    creator: { id: person_id },
    creator_banned: banned,
  } = i.postView;
  const ban = !banned;

  // If its an unban, restore all their data
  const shouldRemoveOrRestoreData = ban ? form.shouldRemoveOrRestoreData : true;
  const expires_at = futureDaysToUnixTime(form.daysUntilExpires);

  return i.onBanPerson({
    person_id,
    ban,
    remove_or_restore_data: shouldRemoveOrRestoreData,
    reason: form.reason,
    expires_at,
  });
}

function handleAppointCommunityMod(i: PostActionBarProps) {
  return i.onAddModToCommunity({
    community_id: i.postView.community.id,
    person_id: i.postView.creator.id,
    added: !i.postView.creator_is_moderator,
  });
}

function handleAppointAdmin(i: PostActionBarProps) {
  return i.onAddAdmin({
    person_id: i.postView.creator.id,
    added: !i.postView.creator_is_admin,
  });
}

function handleTransferCommunity(i: PostActionBarProps) {
  return i.onTransferCommunity({
    community_id: i.postView.community.id,
    person_id: i.postView.creator.id,
  });
}
